import { CookieGetOptions, CookieSetOptions } from 'universal-cookie';
import Dispatcher, { DispatcherManager } from '../../Dispatcher';
import { Service } from '../Service';
import CookieUtil from '../../../utility/CookieUtil';

class CookieManager extends Service {
  private static _instance: any;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  private dispatcherId: string;

  constructor() {
    super();
    this.registerServiceToDispatcher();
  }

  /**
   * Getter for consent url.
   */
  getConsentUrl = (): string => CookieUtil.getConsentUrl();
  /**
   * Setter for consent url.
   * @param url
   */
  setConsentUrl = (url: string) => CookieUtil.setConsentUrl(url);
  /**
   * Setter for default duration.
   * @param defaultDuration
   */
  setDefaultDuration = (defaultDuration: number) => CookieUtil.setDefaultDuration(defaultDuration);
  /**
   * Getter for default duration.
   */
  getDefaultDuration = (): number => CookieUtil.getDefaultDuration();
  /**
   * Get cookie.
   * @param name
   * @param options
   */
  get = (name: string, options?: CookieGetOptions) => CookieUtil.get(name, options);
  /**
   * Remove cookie.
   * @param name
   * @param options
   */
  remove = (name: string, options?: CookieSetOptions) => CookieUtil.remove(name, options);
  /**
   * Set cookie.
   * @param name
   * @param value
   * @param options
   */
  set = (name: string, value: any, options?: CookieSetOptions) => CookieUtil.set(name, value, options);

  /**
   * Clears all analytical cookies.
   */
  clearAllAnalyticalCookies = () => CookieUtil.clearAllAnalyticalCookies();
  /**
   * Clears all functional cookies.
   */
  clearAllFunctionalCookies = () => CookieUtil.clearAllFunctionalCookies();
  /**
   * Clears all required cookies.
   */
  clearAllRequiredCookies = () => CookieUtil.clearAllRequiredCookies();
  /**
   * Changes language locales if functional cookies are accepted.
   * Returns boolean which indicates status.
   * @param locale
   */
  changeLanguage = (locale: string): boolean => CookieUtil.changeLanguage(locale);
  /**
   * Getter for analytics cookie consent.
   */
  getAnalyticsConsent = () => CookieUtil.getAnalyticsConsent();
  /**
   * Getter for functional cookie consent.
   */
  getFunctionalConsent = () => CookieUtil.getFunctionalConsent();
  /**
   * Getter for required cookie consent.
   */
  getRequiredConsent = () => CookieUtil.getRequiredConsent();
  /**
   * Called via dispatcher action checkCookiesBeforeRouteChanges on before route change.
   */
  beforeRouteChange = () => CookieUtil.beforeRouteChange();
  /**
   * Checks if analytics cookies are enabled. Returns `true` if analytics cookies are enabled.
   * If analytics cookies are not enabled, all analytics cookies will be cleared.
   */
  hasAnalyticsCookie = (): boolean => CookieUtil.hasAnalyticsCookie();
  /**
   * Checks if consent url is provided.
   */
  hasConsentUrl = (): boolean => CookieUtil.hasConsentUrl();
  /**
   * Checks if functional cookies are enabled. Returns `true` if functional cookies are enabled.
   * If functional cookies are not enabled, all functional cookies will be cleared.
   */
  hasFunctionalCookie = (): boolean => CookieUtil.hasFunctionalCookie();
  /**
   * Checks if required cookies are enabled. Returns `true` if required cookies are enabled.
   */
  hasRequiredCookie = (): boolean => CookieUtil.hasRequiredCookie();
  /**
   * Getter for locale cookie.
   */
  getLocale = (): string => CookieUtil.getLocale();
  /**
   * Setter for locale cookie. If functional cookies are not enabled. Locale cookie will be cleared.
   * @param locale
   */
  setLocale = (locale: string) => CookieUtil.setLocale(locale);
  /**
   * Getter for theme cookie.
   */
  getTheme = (): string => CookieUtil.getTheme();
  /**
   * Setter for theme cookie. If functional cookies are not enabled. Theme cookie will be cleared.
   * @param theme
   */
  setTheme = (theme: string) => CookieUtil.setTheme(theme);
  /**
   * Getter for dispatcher.
   */
  private getDispatcher = (): DispatcherManager => Dispatcher.appDispatchers[this.dispatcherId] || Dispatcher;
}

const CookieManagerService = CookieManager.Instance;
export default CookieManagerService;
